import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {BarcodeFormat} from '@zxing/library';
import {BaseComponent} from '../../../base/base.component';
import {UserService} from '../../../service/user.service';
import {
    CaronaBlocsAvailabilityGQL,
    CaronaVagaOcupadaGQL,
    OpenCaronaRecordGQL,
    OpenCaronaRecordsGQL,
    RegisterCaronaEntryGQL,
    RegisterCaronaEntryInput,
    RegisterCaronaExitGQL,
    RegisterCaronaExitInput,
    User
} from '../../../../generated/graphql';

// Disponibilidade por bloco (shape enxuto da query CaronaBlocsAvailability).
interface BlocAvail {
    bloc: { id: string; name: string; capacity: number };
    capacity: number;
    occupied: number;
    available: number;
}

@Component({
    selector: 'app-carona-qrcode',
    templateUrl: './carona-qrcode.component.html',
    styleUrls: []
})
export class CaronaQrcodeComponent extends BaseComponent implements OnInit {
    user: User = new User();
    unityId: string;

    // 'entry' = registrar entrada | 'exit' = registrar saída.
    mode: 'entry' | 'exit' = 'entry';

    // Scanner
    allowedFormats = [BarcodeFormat.QR_CODE];
    scannerEnabled = false;
    availableDevices: MediaDeviceInfo[] = [];
    currentDevice: MediaDeviceInfo = undefined;

    // Formulário do colaborador (editável).
    form = {
        employeeName: '',
        re: '',
        registration: '',
        cpf: '',
        licensePlate: '',
        qrRaw: ''
    };

    blocs: BlocAvail[] = [];
    selectedBlocId: string = null;
    // Vaga = texto livre digitado pelo vigilante (entrada normal).
    vaga: string = '';

    // Conferência: valores CRUS lidos do QR (RE / BLOCO / PLACA). null = nada lido.
    qrLido: { re: string; bloco: string; placa: string } = null;
    // Valor do BLOCO lido que NÃO casou com nenhum bloco cadastrado (aviso na tela). null = ok.
    blocNaoEncontrado: string = null;

    // SAÍDA: registros em aberto encontrados (por vaga/RE/placa) para conferência
    // e escolha antes de fechar. Pode haver mais de um (mesma vaga em blocos
    // diferentes, ou o fallback por RE/placa acertar vários). §46 saída-por-vaga.
    openRecords: any[] = [];

    // MODAL saída manual (correção no fluxo de ENTRADA quando já há registro aberto).
    pendingOpenRecord: any = null; // o registro aberto a fechar antes da nova entrada
    manualExit = {exitAt: '', exitNote: ''};

    // MODAL entrada manual (correção no fluxo de SAÍDA quando não há registro aberto).
    manualEntry = {employeeName: '', re: '', licensePlate: '', blocId: null as string, vaga: '', entryAt: '', entryNote: ''};

    constructor(public router: Router,
                public translate: TranslateService,
                public userService: UserService,
                private availabilityGQL: CaronaBlocsAvailabilityGQL,
                private entryGQL: RegisterCaronaEntryGQL,
                private exitGQL: RegisterCaronaExitGQL,
                private openGQL: OpenCaronaRecordGQL,
                private openRecordsGQL: OpenCaronaRecordsGQL,
                private vagaOcupadaGQL: CaronaVagaOcupadaGQL) {
        super(router, translate);
    }

    ngOnInit(): void {
        this.userService.user.subscribe({
            next: data => {
                this.user = data as User;
                // Escopo: unidade do operador logado — mesma fonte das telas
                // unity-scoped (occurrence-list/user usam this.user.unity.id).
                this.unityId = this.user && this.user.unity ? this.user.unity.id : null;
                if (this.unityId) {
                    this.loadAvailability();
                }
            }, error: err => super.onError(err)
        });
    }

    setMode(mode: 'entry' | 'exit'): void {
        this.mode = mode;
        this.openRecords = [];
    }

    // ---------- Disponibilidade ----------
    loadAvailability(): void {
        if (!this.unityId) {
            return;
        }
        this.availabilityGQL
            .fetch({unity: this.unityId}, {fetchPolicy: 'network-only'})
            .subscribe(({data}) => {
                this.blocs = (data.caronaBlocsAvailability as any[]) as BlocAvail[];
            }, err => super.onError(err));
    }

    // Bloco lotado (available<=0) É selecionável — o aviso vira SweetAlert no submit.
    setSelectedBloc(b: BlocAvail): void {
        this.selectedBlocId = b.bloc.id;
    }

    // Bloco selecionado (para checar lotação no submit).
    private blocById(id: string): BlocAvail {
        return this.blocs.find(x => x.bloc.id === id);
    }

    // ---------- Scanner / parser ----------
    toggleScanner(): void {
        this.scannerEnabled = !this.scannerEnabled;
    }

    onCamerasFound(devices: MediaDeviceInfo[]): void {
        this.availableDevices = devices || [];
        if (!this.currentDevice && this.availableDevices.length) {
            this.currentDevice = this.availableDevices[0];
        }
    }

    onScanSuccess(text: string): void {
        this.parseQr(text);
    }

    /**
     * Parser ISOLADO e TOLERANTE do conteúdo do QR — PONTO ÚNICO de troca do payload.
     *
     * Payload REAL da Eurofarma (§46.10): TEXTO PLANO com rótulos + valores, sem o
     * nome do colaborador. Ex.: "RE 45697 BLOCO 07 PLACA DIK8J96".
     * Estratégia: normaliza (trim, colapsa espaços, uppercase) e, para cada rótulo
     * (RE / BLOCO / PLACA) encontrado, pega o TOKEN SEGUINTE como valor — independe
     * da ordem. Rótulo ausente → campo vazio. NENHUM rótulo → avisa e deixa manual.
     * (Compat: ainda aceita um JSON { nome|name, re } antigo, se vier.)
     * Nunca lança.
     */
    parseQr(text: string): void {
        const raw = text || '';
        this.form.qrRaw = raw;

        // Compat opcional: JSON { nome|name, re } antigo — mantém leituras legadas.
        try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object') {
                const nome = parsed.nome || parsed.name || parsed.employeeName;
                const re = parsed.re || parsed.RE || parsed.registration;
                if (nome || re) {
                    if (nome) { this.form.employeeName = String(nome); }
                    if (re) { this.form.re = String(re); }
                    this.qrLido = { re: re ? String(re) : '', bloco: '', placa: '' };
                    this.blocNaoEncontrado = null;
                    return; // reconhecido
                }
            }
        } catch (e) { /* não é JSON — segue para o parser de texto plano */ }

        // Texto plano. Os ÚNICOS rótulos reais são RE e PLACA; o BLOCO é o MIOLO
        // (toda a string entre o valor do RE e o rótulo PLACA), podendo ter N palavras.
        // Colapsa espaços mas PRESERVA o texto/caixa original (miolo é exibido como lido).
        const clean = raw.replace(/\s+/g, ' ').trim();
        const reMatch = clean.match(/\bRE\s+(\S+)/i);       // "RE 45697" → grupo 1 = 45697
        const placaMatch = clean.match(/\bPLACA\s+(\S+)/i); // "PLACA DIK8J96" → grupo 1

        const re = reMatch ? reMatch[1] : '';
        const placa = placaMatch ? placaMatch[1].toUpperCase() : '';

        // BLOCO = substring entre o FIM do valor do RE e o INÍCIO do rótulo PLACA (trim).
        // Sem RE não há como delimitar o miolo com segurança → bloco vazio.
        let bloco = '';
        if (reMatch) {
            const startBloc = (reMatch.index || 0) + reMatch[0].length; // após "RE 45697"
            const endBloc = placaMatch ? (placaMatch.index || 0) : clean.length; // antes de "PLACA"
            if (endBloc > startBloc) {
                bloco = clean.substring(startBloc, endBloc).trim();
            }
        }

        if (!re && !bloco && !placa) {
            // Nenhum rótulo reconhecido — não quebra: vigilante preenche à mão.
            this.qrLido = null;
            this.blocNaoEncontrado = null;
            super.showMessage(
                this.translate.instant('atencao'),
                this.translate.instant('qrnaoreconhecido'),
                'warning'
            );
            return;
        }

        // Preenche o formulário (tudo editável; NOME não vem do QR → fica em branco).
        if (re) { this.form.re = re; }
        if (placa) { this.form.licensePlate = placa; }

        // Painel de conferência com os valores CRUS lidos.
        this.qrLido = { re, bloco, placa };

        // Casa o BLOCO lido com os cadastrados (só match exato; senão, não seleciona).
        this.matchBloc(bloco);
    }

    /**
     * Casa o MIOLO de bloco lido (string inteira, N palavras) com os blocos
     * cadastrados da unidade. A palavra "BLOCO" às vezes FAZ parte do nome
     * ("BLOCO 07") e às vezes NÃO ("BROOKLIN"), então testa DUAS hipóteses:
     *   (a) o miolo INTEIRO normalizado (ex.: "BLOCO 07", "BLOCO BROOKLIN");
     *   (b) o miolo SEM o "BLOCO" inicial, se houver (ex.: "07", "BROOKLIN").
     * Match EXATO (normalizado): uppercase + trim + colapsa espaços. Sem "contains".
     * Sem match → não seleciona nada + aviso. Miolo vazio → nada a casar, sem aviso.
     */
    private matchBloc(blocoValue: string): void {
        this.selectedBlocId = null;
        this.blocNaoEncontrado = null;
        if (!blocoValue) {
            return;
        }
        const norm = (s: string) => (s || '').replace(/\s+/g, ' ').trim().toUpperCase();
        const whole = norm(blocoValue);
        const withoutPrefix = whole.replace(/^BLOCO\s+/, '');
        const candidates = withoutPrefix !== whole ? [whole, withoutPrefix] : [whole];
        const found = this.blocs.find(b => candidates.indexOf(norm(b.bloc.name)) >= 0);
        if (found) {
            this.selectedBlocId = found.bloc.id;
        } else {
            this.blocNaoEncontrado = blocoValue;
        }
    }

    // ---------- ENTRADA ----------
    submitEntry(): void {
        // NOME é OPCIONAL (não vem no QR). RE é OBRIGATÓRIO: é o identificador real
        // (vem no QR e é por ele que a saída acha o registro aberto).
        const stringError = this.validateField([
            {value: this.form.re, text: `RE<br>`},
            {value: this.form.licensePlate, text: `${this.translate.instant('placa')}<br>`},
            {value: this.selectedBlocId, text: `${this.translate.instant('bloc')}<br>`}
        ]);
        if (!super.isNullOrUndefined(stringError)) {
            super.showMessage(this.translate.instant('atencao'),
                `${this.translate.instant('preenchaoscampos')} <br> ${stringError}`);
            return;
        }
        // Encadeia as confirmações SEM travar: (1) vaga já ocupada → (2) bloco lotado
        // → registra. Cada confirmação chama o próximo passo só no "Sim"; cancelar
        // aborta. proceedEntry é chamado UMA vez ao final (sem duplicar registro).
        this.checkVagaThen(this.vaga, () => {
            const b = this.blocById(this.selectedBlocId);
            if (b && b.available <= 0) {
                super.confirmMessage(this.translate.instant('atencao'),
                    this.translate.instant('blocolotadoconfirmar'),
                    () => this.proceedEntry());
                return;
            }
            this.proceedEntry();
        });
    }

    /**
     * Checagem de VAGA OCUPADA (§46 Fatia 2) — só AVISO, nunca trava. Se a vaga
     * (após trim) não for vazia, consulta caronaVagaOcupada(blocId, vaga); se
     * ocupada, pede confirmação (SweetAlert) e só chama cb no "Sim"; se livre ou
     * vazia, chama cb direto. cb encapsula o resto do fluxo (lotação + registro).
     */
    private checkVagaThen(vaga: string, cb: () => void): void {
        const v = (vaga || '').trim();
        if (!v) {
            cb();
            return;
        }
        this.vagaOcupadaGQL.fetch({unity: this.unityId, vaga: v}, {fetchPolicy: 'network-only'})
            .subscribe(({data}) => {
                if (data && data.caronaVagaOcupada) {
                    super.confirmMessage(this.translate.instant('atencao'),
                        this.translate.instant('vagaocupadaconfirmar', {vaga: v}), cb);
                } else {
                    cb();
                }
            }, err => super.onError(err));
    }

    // Segue o fluxo de entrada normal (§46.6 passo 2: se já há registro em aberto
    // para o mesmo colaborador, direciona à saída manual antes de duplicar).
    private proceedEntry(): void {
        super.toggleLoader(true);
        this.openGQL.fetch({unity: this.unityId, term: this.form.re}, {fetchPolicy: 'network-only'})
            .subscribe(({data}) => {
                super.toggleLoader(false);
                if (data && data.openCaronaRecord) {
                    this.pendingOpenRecord = data.openCaronaRecord;
                    this.manualExit = {exitAt: this.nowLocal(), exitNote: ''};
                    super.showModal('manualExitModal');
                } else {
                    this.doEntry(null, false, null);
                }
            }, err => {
                super.toggleLoader(false);
                super.onError(err);
            });
    }

    // Cria o registro de entrada. manual=true vem do modal de correção.
    doEntry(entryAt: string, manual: boolean, entryNote: string): void {
        const data: RegisterCaronaEntryInput = {
            // Nome opcional: vazio → null (o back não aceita mais '' e grava NULL).
            employeeName: this.form.employeeName || null,
            re: this.form.re,
            registration: this.form.registration || null,
            cpf: this.form.cpf || null,
            licensePlate: this.form.licensePlate,
            qrRaw: this.form.qrRaw || null,
            bloc: {id: manual ? this.manualEntry.blocId : this.selectedBlocId},
            // Vaga = texto livre (opcional). Normal usa this.vaga; manual usa manualEntry.vaga.
            vaga: (manual ? this.manualEntry.vaga : this.vaga) || null,
            entryAt: entryAt ? new Date(entryAt).toISOString() : null,
            isManualEntry: manual,
            entryNote: entryNote || null
            // unity NÃO enviada — derivada do bloco no back-end.
        };
        super.toggleLoader(true);
        this.entryGQL.mutate({data}).subscribe(() => {
            super.toggleLoader(false);
            super.showMessage(this.translate.instant('success'),
                this.translate.instant('entradaregistrada'), 'success');
            this.resetForm();
            this.loadAvailability();
        }, err => {
            super.toggleLoader(false);
            // Lotação e demais regras do back chegam como mensagem clara.
            super.onError(err);
        });
    }

    // ---------- SAÍDA ----------
    submitExit(): void {
        // Termo único digitado (VAGA, RE ou placa). O back casa por vaga
        // normalizada OU re/registration/cpf/placa e retorna a LISTA de abertos.
        const term = this.form.re || this.form.licensePlate;
        if (super.isNullOrUndefined(term)) {
            super.showMessage(this.translate.instant('atencao'),
                this.translate.instant('informereplaca'));
            return;
        }
        super.toggleLoader(true);
        this.openRecordsGQL.fetch({unity: this.unityId, term}, {fetchPolicy: 'network-only'})
            .subscribe(({data}) => {
                super.toggleLoader(false);
                const list = (data && data.openCaronaRecords) || [];
                if (list.length > 0) {
                    this.openRecords = list; // mostra a lista p/ conferência/escolha
                } else {
                    // §46.6 passo 4: nenhum aberto → registrar entrada manual.
                    // Pré-preenche RE com o termo buscado; nome/placa o vigilante informa.
                    this.openRecords = [];
                    this.manualEntry = {
                        employeeName: '', re: this.form.re || '', licensePlate: '',
                        blocId: null, vaga: '', entryAt: this.nowLocal(), entryNote: ''
                    };
                    super.showModal('manualEntryModal');
                }
            }, err => {
                super.toggleLoader(false);
                super.onError(err);
            });
    }

    // Confirma a saída do registro ESCOLHIDO na lista.
    confirmExit(record: any): void {
        if (!record) {
            return;
        }
        this.doExit(record.id, null, false, null);
    }

    doExit(recordId: string, exitAt: string, manual: boolean, exitNote: string): void {
        const data: RegisterCaronaExitInput = {
            record: {id: recordId},
            exitAt: exitAt ? new Date(exitAt).toISOString() : null,
            isManualExit: manual,
            exitNote: exitNote || null
        };
        super.toggleLoader(true);
        this.exitGQL.mutate({data}).subscribe(() => {
            super.toggleLoader(false);
            super.showMessage(this.translate.instant('success'),
                this.translate.instant('saidaregistrada'), 'success');
            this.openRecords = [];
            this.resetForm();
            this.loadAvailability();
        }, err => {
            super.toggleLoader(false);
            super.onError(err);
        });
    }

    // ---------- Modal: SAÍDA MANUAL (correção no fluxo de entrada) ----------
    submitManualExit(): void {
        if (super.isNullOrUndefined(this.manualExit.exitNote)) {
            super.showMessage(this.translate.instant('atencao'),
                this.translate.instant('justificativaobrigatoria'));
            return;
        }
        const rec = this.pendingOpenRecord;
        super.toggleLoader(true);
        this.exitGQL.mutate({
            data: {
                record: {id: rec.id},
                exitAt: this.manualExit.exitAt ? new Date(this.manualExit.exitAt).toISOString() : null,
                isManualExit: true,
                exitNote: this.manualExit.exitNote
            }
        }).subscribe(() => {
            super.toggleLoader(false);
            super.closeModal('manualExitModal');
            this.pendingOpenRecord = null;
            // segue a entrada original que o vigilante queria registrar
            this.doEntry(null, false, null);
        }, err => {
            super.toggleLoader(false);
            super.onError(err);
        });
    }

    // ---------- Modal: ENTRADA MANUAL (correção no fluxo de saída) ----------
    submitManualEntry(): void {
        // Nome opcional / RE obrigatório: o RE (não mais o nome) é o que garante a
        // identificação do registro criado no fluxo de saída-sem-entrada.
        const stringError = this.validateField([
            {value: this.manualEntry.re, text: `RE<br>`},
            {value: this.manualEntry.blocId, text: `${this.translate.instant('bloc')}<br>`},
            {value: this.manualEntry.entryNote, text: `${this.translate.instant('justificativa')}<br>`}
        ]);
        if (!super.isNullOrUndefined(stringError)) {
            super.showMessage(this.translate.instant('atencao'),
                `${this.translate.instant('preenchaoscampos')} <br> ${stringError}`);
            return;
        }
        // Vaga ocupada (aviso) → bloco lotado (aviso) → registra. Não trava, não duplica.
        this.checkVagaThen(this.manualEntry.vaga, () => {
            const b = this.blocById(this.manualEntry.blocId);
            if (b && b.available <= 0) {
                super.confirmMessage(this.translate.instant('atencao'),
                    this.translate.instant('blocolotadoconfirmar'),
                    () => this.proceedManualEntry());
                return;
            }
            this.proceedManualEntry();
        });
    }

    private proceedManualEntry(): void {
        const data: RegisterCaronaEntryInput = {
            // Nome opcional: vazio → null.
            employeeName: this.manualEntry.employeeName || null,
            re: this.manualEntry.re || null,
            licensePlate: this.manualEntry.licensePlate || null,
            qrRaw: this.form.qrRaw || null,
            bloc: {id: this.manualEntry.blocId},
            // Vaga = texto livre (opcional).
            vaga: this.manualEntry.vaga || null,
            entryAt: this.manualEntry.entryAt ? new Date(this.manualEntry.entryAt).toISOString() : null,
            isManualEntry: true,
            entryNote: this.manualEntry.entryNote
        };
        super.toggleLoader(true);
        this.entryGQL.mutate({data}).subscribe(({data: res}) => {
            super.toggleLoader(false);
            super.closeModal('manualEntryModal');
            // entrada criada → agora fecha a saída que o vigilante queria registrar
            const created: any = res.registerCaronaEntry;
            this.doExit(created.id, null, false, null);
        }, err => {
            super.toggleLoader(false);
            super.onError(err);
        });
    }

    // ---------- utils ----------
    resetForm(): void {
        this.form = {employeeName: '', re: '', registration: '', cpf: '', licensePlate: '', qrRaw: ''};
        this.selectedBlocId = null;
        this.vaga = '';
        this.qrLido = null;
        this.blocNaoEncontrado = null;
    }

    // 'YYYY-MM-DDTHH:mm' local, para o default do <input type="datetime-local">.
    nowLocal(): string {
        const d = new Date();
        const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
}
